import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Ticket, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  Plus,
  Star,
  StarIcon,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import SupportTicketForm from '@/components/support/SupportTicketForm';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';

interface SupportTicket {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface TicketResponse {
  id: string;
  response_text: string;
  created_at: string;
  responded_by: string;
  admin_name?: string;
}

interface TicketRating {
  id: string;
  rating: number;
  feedback: string | null;
  created_at: string;
}

const MyTickets: React.FC = () => {
  const navigate = useNavigate();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const isMobile = useIsMobile();
  // Ensure consistent user resolution - prioritize the user that's actually authenticated
  const currentUser = (internalUser && !supabaseUser) ? internalUser : 
                     (!internalUser && supabaseUser) ? supabaseUser :
                     internalUser ? internalUser : supabaseUser; // Default to internal if both exist
  
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [ticketRating, setTicketRating] = useState<TicketRating | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (currentUser?.id) {
      fetchMyTickets();
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketResponses(selectedTicket.id);
      fetchTicketRating(selectedTicket.id);
    }
  }, [selectedTicket]);

  const fetchMyTickets = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load your tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTicketResponses = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('support_ticket_responses')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Get admin info separately
      const adminIds = [...new Set(data?.map(response => response.responded_by).filter(Boolean) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', adminIds);

      const responsesWithAdminInfo = data?.map(response => {
        const profile = profiles?.find(p => p.id === response.responded_by);
        return {
          ...response,
          admin_name: profile?.username || 
                     `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 
                     'Support Team'
        };
      }) || [];

      setResponses(responsesWithAdminInfo);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load ticket responses');
    }
  };

  const fetchTicketRating = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('support_ticket_ratings')
        .select('*')
        .eq('ticket_id', ticketId)
        .eq('user_id', currentUser?.id)
        .maybeSingle();

      if (error) throw error;
      setTicketRating(data);
    } catch (error) {
      console.error('Error fetching ticket rating:', error);
    }
  };

  const submitRating = async () => {
    if (!selectedTicket || !currentUser?.id || rating === 0) return;

    try {
      const { error } = await supabase
        .from('support_ticket_ratings')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: currentUser.id,
          rating,
          feedback: feedback.trim() || null
        });

      if (error) throw error;

      toast.success('Thank you for your feedback!');
      setShowRatingForm(false);
      setRating(0);
      setFeedback('');
      fetchTicketRating(selectedTicket.id);
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    }
  };

  const submitReply = async () => {
    if (!selectedTicket || !currentUser?.id || !replyText.trim()) return;

    try {
      const { error } = await supabase
        .from('support_ticket_responses')
        .insert({
          ticket_id: selectedTicket.id,
          response_text: replyText.trim(),
          responded_by: currentUser.id,
          is_admin_response: false
        });

      if (error) throw error;

      toast.success('Reply sent successfully!');
      setShowReplyForm(false);
      setReplyText('');
      fetchTicketResponses(selectedTicket.id);
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-blue-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <AlertCircle className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (!currentUser) {
    return (
      <>
        {isMobile ? <MobileHeader /> : <Header />}
        <div className="min-h-screen bg-[#0d1117] pt-20 flex items-center justify-center"> {/* Added pt-20 for fixed header */}
          <Card className="bg-gray-900 border-gray-800 max-w-md">
            <CardContent className="p-6 text-center">
              <h2 className="text-xl font-bold text-white mb-2">Authentication Required</h2>
              <p className="text-gray-400">Please log in to view your support tickets.</p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        {isMobile ? <MobileHeader /> : <Header />}
        <div className="min-h-screen bg-[#0d1117] pt-20 flex items-center justify-center"> {/* Added pt-20 for fixed header */}
          <div className="text-white">Loading your tickets...</div>
        </div>
        <Footer />
      </>
    );
  }

  if (showNewTicketForm) {
    return (
      <>
        {isMobile ? <MobileHeader /> : <Header />}
        <div className="min-h-screen bg-[#0d1117] pt-20 py-8"> {/* Added pt-20 for fixed header */}
          <div className="container mx-auto px-4">
            <SupportTicketForm onClose={() => {
              setShowNewTicketForm(false);
              fetchMyTickets();
            }} />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      {isMobile ? <MobileHeader /> : <Header />}
      <div className="min-h-screen bg-[#0d1117] pt-20 py-8"> {/* Added pt-20 for fixed header */}
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="text-gray-400 border-gray-600 hover:bg-gray-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Ticket className="h-6 w-6 text-sports-green" />
                My Support Tickets
              </h1>
            </div>
            <Button
              onClick={() => setShowNewTicketForm(true)}
              className="bg-sports-green text-black hover:bg-sports-green/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Ticket
            </Button>
          </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tickets List */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Your Tickets ({tickets.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedTicket?.id === ticket.id
                      ? 'border-sports-green bg-gray-800'
                      : 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-white">{ticket.ticket_number}</div>
                      <div className="text-sm text-gray-400">{ticket.subject}</div>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={`${getPriorityColor(ticket.priority)} text-white`}>
                        {ticket.priority}
                      </Badge>
                      <Badge className={`${getStatusColor(ticket.status)} text-white flex items-center gap-1`}>
                        {getStatusIcon(ticket.status)}
                        {ticket.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <div className="capitalize">{ticket.category.replace('_', ' ')}</div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
              {tickets.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-4">You haven't submitted any support tickets yet.</p>
                  <Button
                    onClick={() => setShowNewTicketForm(true)}
                    className="bg-sports-green text-black hover:bg-sports-green/90"
                  >
                    Submit Your First Ticket
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ticket Details */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {selectedTicket ? `Ticket Details` : 'Select a Ticket'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedTicket ? (
                <div className="space-y-4">
                  {/* Ticket Info */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-white text-lg">{selectedTicket.ticket_number}</h3>
                        <p className="text-gray-400">{selectedTicket.subject}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge className={`${getPriorityColor(selectedTicket.priority)} text-white`}>
                          {selectedTicket.priority}
                        </Badge>
                        <Badge className={`${getStatusColor(selectedTicket.status)} text-white flex items-center gap-1`}>
                          {getStatusIcon(selectedTicket.status)}
                          {selectedTicket.status}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-400">
                      <div className="flex justify-between">
                        <span>Category: <span className="capitalize">{selectedTicket.category.replace('_', ' ')}</span></span>
                        <span>Created: {new Date(selectedTicket.created_at).toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Original Message:</h4>
                      <p className="text-gray-300">{selectedTicket.description}</p>
                    </div>
                  </div>

                  {/* Responses */}
                  {responses.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-white font-medium">Conversation:</h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {responses.map((response) => (
                          <div 
                            key={response.id} 
                            className={`p-3 rounded-lg ${
                              response.responded_by === currentUser?.id
                                ? 'bg-blue-900/30 border-l-4 border-blue-500 ml-4'
                                : 'bg-gray-800 border-l-4 border-sports-green'
                            }`}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className={`text-sm font-medium ${
                                response.responded_by === currentUser?.id
                                  ? 'text-blue-300'
                                  : 'text-sports-green'
                              }`}>
                                {response.responded_by === currentUser?.id 
                                  ? 'You' 
                                  : response.admin_name}
                              </div>
                              <div className="text-xs text-gray-400">
                                {new Date(response.created_at).toLocaleString()}
                              </div>
                            </div>
                            <div className="text-sm text-gray-300">{response.response_text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reply Section - Only for Open/In Progress tickets */}
                  {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                    <div className="space-y-3">
                      {!showReplyForm ? (
                        <Button
                          onClick={() => setShowReplyForm(true)}
                          className="bg-blue-600 text-white hover:bg-blue-700"
                          size="sm"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Add Reply
                        </Button>
                      ) : (
                        <div className="space-y-3">
                          <h4 className="text-white font-medium">Your Reply:</h4>
                          <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your message here..."
                            className="bg-gray-800 border-gray-700 text-white"
                            rows={4}
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={submitReply}
                              disabled={!replyText.trim()}
                              className="bg-sports-green text-black hover:bg-sports-green/90"
                              size="sm"
                            >
                              Send Reply
                            </Button>
                            <Button
                              onClick={() => {
                                setShowReplyForm(false);
                                setReplyText('');
                              }}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rating Section for Resolved/Closed Tickets */}
                  {(selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') && (
                    <div className="space-y-3">
                      {ticketRating ? (
                        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                          <h4 className="text-green-300 font-medium mb-2 flex items-center gap-2">
                            <Star className="h-4 w-4" />
                            Your Rating
                          </h4>
                          <div className="flex items-center gap-2 mb-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= ticketRating.rating
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-gray-500'
                                }`}
                              />
                            ))}
                            <span className="text-green-300 text-sm">
                              ({ticketRating.rating}/5 stars)
                            </span>
                          </div>
                          {ticketRating.feedback && (
                            <p className="text-sm text-gray-300">{ticketRating.feedback}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Rated on {new Date(ticketRating.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ) : (
                        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                          <h4 className="text-blue-300 font-medium mb-2">Rate Our Support</h4>
                          <p className="text-sm text-gray-300 mb-3">
                            How would you rate the support you received for this ticket?
                          </p>
                          {!showRatingForm ? (
                            <Button
                              onClick={() => setShowRatingForm(true)}
                              className="bg-blue-600 text-white hover:bg-blue-700"
                              size="sm"
                            >
                              <Star className="h-4 w-4 mr-2" />
                              Rate Support
                            </Button>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className="p-1 hover:scale-110 transition-transform"
                                  >
                                    <Star
                                      className={`h-6 w-6 ${
                                        star <= rating
                                          ? 'text-yellow-400 fill-yellow-400'
                                          : 'text-gray-500 hover:text-yellow-300'
                                      }`}
                                    />
                                  </button>
                                ))}
                              </div>
                              <Textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Optional: Share your feedback about the support experience..."
                                className="bg-gray-800 border-gray-700 text-white"
                                rows={3}
                              />
                              <div className="flex gap-2">
                                <Button
                                  onClick={submitRating}
                                  disabled={rating === 0}
                                  className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                  size="sm"
                                >
                                  Submit Rating
                                </Button>
                                <Button
                                  onClick={() => {
                                    setShowRatingForm(false);
                                    setRating(0);
                                    setFeedback('');
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTicket.status === 'closed' && !ticketRating && (
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                      <p className="text-gray-400 text-sm">
                        This ticket has been closed. If you need further assistance, please submit a new ticket.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a ticket to view details and responses</p>
                </div>
              )}
            </CardContent>
           </Card>
         </div>
       </div>
       </div>
       <Footer />
     </>
   );
 };

 export default MyTickets;